import axios from "axios";
import { useQuery } from "react-query";
import { Config } from "../config";

const userBaseUrl = Config.serviceUrls.userBaseUrl;
const sendBirdApiUrl = Config.serviceUrls.sendBirdApiUrl;
const patientBaseUrl = Config.serviceUrls.patientSchedulingBaseUrl;

export const getStaffMembersList = async ({ queryKey }) => {
  const [, OwnerId, OfficeIds, DesignationIds, SearchByName, PageNo, PageSize] =
    queryKey;

  const params = new URLSearchParams();
  if (OwnerId != null && OwnerId !== "") {
    params.append("ownerId", String(OwnerId));
  }
  params.append("SearchTerm", SearchByName ?? "");
  params.append("IsForChat", "true");
  params.append("pageNumber", String(PageNo ?? 1));
  params.append("pageSize", String(PageSize ?? 30));

  (Array.isArray(OfficeIds) ? OfficeIds : []).forEach((id) => {
    if (id != null && id !== "") params.append("officeIds", String(id));
  });
  (Array.isArray(DesignationIds) ? DesignationIds : []).forEach((id) => {
    if (id != null && id !== "") params.append("designationIds", String(id));
  });

  const response = await axios.get(
    `${userBaseUrl}/Staff/GetDistinctUsersByOwnerId?${params.toString()}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

const MESSENGER_ROLE_FILTER_STAFF_PAGE_SIZE = 500;
/** Cap pages so very large orgs do not trigger unbounded sequential requests. */
const MESSENGER_ROLE_FILTER_MAX_PAGES = 40;

/**
 * Loads all staff rows for an owner (paged) so team chat role filtering can map
 * userId → designationIds even when Sendbird channel JSON is incomplete.
 * @param {string|number} ownerId
 * @returns {Promise<{ data: unknown[] }>}
 */
export async function fetchAllStaffMembersForMessengerRoleFilter(ownerId) {
  if (ownerId == null || ownerId === "") {
    return { data: [] };
  }
  const pageSize = MESSENGER_ROLE_FILTER_STAFF_PAGE_SIZE;
  const first = await getStaffMembersList({
    queryKey: ["messanger-staff-role-filter", ownerId, [], [], "", 1, pageSize],
  });
  const totalReported = first?.pagination?.totalPages ?? 1;
  const totalPages = Math.min(
    Math.max(1, totalReported),
    MESSENGER_ROLE_FILTER_MAX_PAGES,
  );
  let rows = [...(first?.data ?? [])];
  for (let p = 2; p <= totalPages; p += 1) {
    const res = await getStaffMembersList({
      queryKey: [
        "messanger-staff-role-filter",
        ownerId,
        [],
        [],
        "",
        p,
        pageSize,
      ],
    });
    rows = rows.concat(res?.data ?? []);
  }
  return { data: rows };
}

export const useGetStaffMembers = (
  ownerId,
  officeIds,
  designationIds,
  SearchByName,
  PageNo,
  PageSize,
  options = {},
) => {
  return useQuery(
    [
      "messanger-staff-members",
      ownerId,
      officeIds,
      designationIds,
      SearchByName,
      PageNo,
      PageSize,
    ],
    getStaffMembersList,
    options,
  );
};
export const createCallLog = async (params) => {
  const response = await axios.post(
    `${patientBaseUrl}/Appointment/ForCaller`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.data;
};

export const getCallLog = async (id) => {
  const response = await axios.get(
    `${patientBaseUrl}/Appointment/ByCaller?SendbirdCallId=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.data;
};
export const getMessangerOwnerList = async () => {
  const response = await axios.get(
    `${userBaseUrl}/User/OwnerListByMessengerPreference`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.data;
};

export const useGetMessangerOwnerList = (ownerId, options = {}) => {
  return useQuery(
    ["messanger-owner-list", ownerId],
    () => getMessangerOwnerList(),
    options,
  );
};

export const getExternalMembers = async (SearchTerm, OwnerId) => {
  const response = await axios.post(
    `${userBaseUrl}/User/ExternalMessengerList`,
    {
      SearchTerm,
      OwnerId,
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.data;
};

export const createSendbirdUser = async (postData) => {
  const response = await axios.post(`${sendBirdApiUrl}/v3/users`, postData, {
    headers: {
      "Api-Token": process.env.REACT_APP_SENDBIRD_API_TOKEN,
    },
  });

  return response.data;
};

export const updateSendbirdUserStatus = async (userId, metadata = {}) => {
  const payload = {
    metadata: {
      ...metadata,
    },
    upsert: true,
  };
  const response = await axios.put(
    `${sendBirdApiUrl}/v3/users/${userId}/metadata`,
    payload,
    {
      headers: {
        "Api-Token": process.env.REACT_APP_SENDBIRD_API_TOKEN,
      },
    },
  );

  return response.data;
};
export const getSendbirdUserDetail = async (userId) => {
  const response = await axios.get(`${sendBirdApiUrl}/v3/users/${userId}`, {
    headers: {
      "Api-Token": process.env.REACT_APP_SENDBIRD_API_TOKEN,
    },
  });

  return response.data;
};
export const getSendbirdUnreadCount = async (userId) => {
  const response = await axios.get(
    `${sendBirdApiUrl}/v3/users/${userId}/unread_message_count`,
    {
      params: {
        super_mode: "nonsuper",
      },
      headers: {
        "Api-Token": process.env.REACT_APP_SENDBIRD_API_TOKEN,
      },
    },
  );

  return response.data;
};

export const useGetSendbirdUnreadCount = (userId, options = {}) => {
  return useQuery(
    ["sendird-unread-count", userId],
    () => getSendbirdUnreadCount(userId),
    options,
  );
};

export const removeMemberFromGroupChannel = async (channelUrl, userId) => {
  const encodedChannelUrl = encodeURIComponent(channelUrl);

  await axios.post(
    `${sendBirdApiUrl}/v3/group_channels/${encodedChannelUrl}/ban`,
    {
      user_id: userId.toString(),
      seconds: 1, // Temporary ban (1 second)
      description: "Removed from group by creator",
    },
    {
      headers: {
        "Api-Token": process.env.REACT_APP_SENDBIRD_API_TOKEN,
        "Content-Type": "application/json",
      },
    },
  );

  // Step 2: Immediately unban the user (so they can be re-invited if needed)
  const response = await axios.delete(
    `${sendBirdApiUrl}/v3/group_channels/${encodedChannelUrl}/ban/${userId}`,
    {
      headers: {
        "Api-Token": process.env.REACT_APP_SENDBIRD_API_TOKEN,
      },
    },
  );

  return response.data;
};
